import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    createContext,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../context';
import {
    List,
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
} from 'react-virtualized';
import hasPermission from '../../Shared/hasPermission';
import PracticeListItem from './PracticeListItem/PracticeListItem';
import {
    getStatusBackgroundClass,
    getStatusBadgeText,
} from './PracticeListStatusHelper';

const page = 1;
export const PracticeListPermissionsContext = createContext();

function PracticesList() {
    const dispatch = useDispatch();

    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );

    // TODO: this is part of the data sync, so when that is refactored refactor this as well
    const {
        assignedFilterUser,
        bookmarked,
        includeTestPractices,
        includeDormantPractices,
        onlyShowAssigned,
        practiceFilter,
        practiceTab,
        searchCustomIds,
        searchGroups,
        searchNotes,
        searchPartnerOfferings,
        searchPracticePims,
    } = useSelector(
        (state) => ({
            assignedFilterUser: state.practice.assignedFilterUser, // null or a guid to filter practice list by the given userId
            bookmarked: state.practice.bookmarked, // bool to filter for bookmarked practices
            includeTestPractices: state.practice.includeTestPractices, // bool tied to the view test practices toggle
            includeDormantPractices: state.practice.includeDormantPractices, // bool tied to the view dormant practices toggle
            onlyShowAssigned: state.practice.onlyShowAssigned, // bool tied to the filter assigned practices button
            practiceFilter: state.practice.filter, // string (defualt empty) that filters practice list based on the input
            practiceTab: state.practice.practiceLink, // string (default is 'all') tied to the practices filter selector
            searchCustomIds: state.practice.searchCustomIds, // bool tied to Search Partner Ids toggle in the text search section
            searchGroups: state.practice.searchGroups, // bool tied to the Serch Practice Groups toggle in the text search section
            searchNotes: state.practice.searchNotes, // bool tied to the Search Practice Notes toggle in the text search section
            searchPartnerOfferings: state.practice.searchPartnerOfferings, // bool tied to the Search Partner Offerings toggle in the text search section
            searchPracticePims: state.practice.searchPracticePims, // bool tied to the Search Practice Pims toggle in the text search section,
        }),
        shallowEqual
    );
    // TODO: improve the below behavior when doing the data sync optimization
    const triggerRefresh = useSelector((state) => state.toggle.triggerRefresh); // bool (defualt false) that gets toggled in different places in teh app, then toggle again in the 30 sec data sync

    const [practices, setPractices] = useState([]);

    const shouldPracticeListUpdate = (newPracticeList) => {
        if (practices.length !== newPracticeList.length) return true;
        const practiceTemplate = {
            id: '',
            name: '',
            assignedAccountId: '',
            ignoreStatus: '',
            isMuted: '',
            partiallyActive: '',
            hasHeartbeatError: '',
            staleValue: '',
            hasScheduleIssue: '',
            isBookmarked: false,
            notificationCount: 0,
            groupName: '',
        };

        const normalizePractice = (practice) => {
            const normalized = {};
            for (const key in practiceTemplate) {
                normalized[key] = practice[key] ?? practiceTemplate[key];
            }

            return normalized;
        };

        const normalizedPractices = practices.map(normalizePractice);
        const normalizedNewPractices = newPracticeList.map(normalizePractice);

        const oldPracticesMap = {};
        normalizedPractices.forEach(practice => {
            oldPracticesMap[practice.id] = practice;
        });

        for (const newPractice of normalizedNewPractices) {
            const oldPractice = oldPracticesMap[newPractice.id];
            if (!oldPractice || !shallowEqual(oldPractice, newPractice)) {
                return true;
            }
        }

        return false;
    };
    const shouldRenderPracticeList = useRef(true);

    const can = useCallback(
        (permission) => hasPermission(permission, permissions),
        [permissions]
    );

    const canDo = useMemo(
        () => ({
            canSearchGroups: can('SearchGroups'),
            canSeePracticeListStatusIcons: can('PracticeListStatusIcons'),
            canAssignUsers: can('Assignment'),
            canMutePractices: can('Muting'),
        }),
        [can]
    );

    // TODO: if a component dispatches show only assigned and assigned user should be tied and batched together somehow
    useEffect(() => {
        dispatch(
            actions.practice.getPractices(
                practiceTab,
                practiceFilter,
                bookmarked,
                onlyShowAssigned,
                assignedFilterUser,
                includeTestPractices,
                includeDormantPractices,
                searchNotes,
                searchGroups,
                searchCustomIds,
                searchPartnerOfferings,
                searchPracticePims,
                page
            )
        ).then((res) => {
            const newPracticeList = res?.data || [];
            shouldRenderPracticeList.current =
                shouldPracticeListUpdate(newPracticeList);
            console.log("shouldRenderPracticeList.current", shouldRenderPracticeList.current)
            if (shouldRenderPracticeList.current) setPractices(newPracticeList);
        });
        // eslint-disable-next-line
    }, [
        dispatch,
        assignedFilterUser,
        practiceTab,
        practiceFilter,
        bookmarked,
        onlyShowAssigned,
        includeTestPractices,
        includeDormantPractices,
        searchNotes,
        searchGroups,
        searchCustomIds,
        searchPartnerOfferings,
        searchPracticePims,
        triggerRefresh,
    ]);

    const cache = useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 100,
        })
    );

    const renderPractice = (p) => {
        const statusContent = {
            statusBackgroundClass: getStatusBackgroundClass(p),
            statusBadgeText: getStatusBadgeText(p),
        };

        p.practiceMute = p.practiceMute || { muteUntil: 0, muteNote: null };

        const itemProps = {
            ...statusContent,
            practice: p,
        };

        return (
            <PracticeListItem
                key={p.id}
                filter={practiceFilter}
                searchCustomIds={searchCustomIds}
                searchPartnerOfferings={searchPartnerOfferings}
                searchPracticePims={searchPracticePims}
                {...itemProps}
            />
        );
    };

    return (
        <PracticeListPermissionsContext.Provider value={canDo}>
            <AutoSizer>
                {({ width, height }) => (
                    <List
                        width={width}
                        height={height}
                        rowHeight={cache.current.rowHeight}
                        deferredMeasurementCache={cache.current}
                        rowCount={practices.length}
                        rowRenderer={({ key, index, style, parent }) => {
                            const practice = practices[index];

                            return (
                                <CellMeasurer
                                    key={key}
                                    cache={cache.current}
                                    parent={parent}
                                    columnIndex={0}
                                    rowIndex={index}>
                                    <div style={style} className="mx-2">
                                        {renderPractice(practice)}
                                    </div>
                                </CellMeasurer>
                            );
                        }}
                    />
                )}
            </AutoSizer>
        </PracticeListPermissionsContext.Provider>
    );
}

export default React.memo(PracticesList);
